/**
 * Statusautomaten. Die SENDUNG fuehrt den Status (Arbeitseinheit), der
 * AUFTRAG leitet seinen Status aus seinen Sendungen ab. Quelle: Prozessdesign
 * docs/storeos/research/04-prozess-pick-pack-versand.md, Abschnitt 1.
 *
 * Kuerzung gegenueber 04: `retoure_offen` ist KEIN Sendungsstatus mehr. Eine
 * Sendung endet in `zustellproblem`; der Ruecklauf ist eine Retoure (eigenes
 * Objekt mit eigenem Status). Zwei Objekte fuer denselben Zustand waeren zwei
 * Wahrheiten.
 */

export const ROUTEN = ['lager', 'intern_bb', 'partner'] as const;
export type Route = (typeof ROUTEN)[number];

/**
 * Woher die Route eines Artikels stammt (R100). `manuell` = in StoreOS gesetzt (auch jeder Altbestand), wird von
 * keinem Abgleich ueberschrieben. `kanal` = Woo-Meta `_storeos_route`, `konfiguration` = bekannte Produkt-ID aus
 * ROUTE_PARTNER_PRODUKTE/ROUTE_INTERN_BB_PRODUKTE, `heuristik` = Namensregel (routeHeuristik).
 */
export const ROUTE_QUELLEN = ['manuell', 'kanal', 'konfiguration', 'heuristik'] as const;
export type RouteQuelle = (typeof ROUTE_QUELLEN)[number];

export const SENDUNG_STATUS = [
  'neu',               // importiert, noch nicht geprueft
  'wartet_bestand',    // Lagerware fehlt, Reservierung offen
  'wartet_produktion', // Route intern_bb: Cover bei B&B / Binden
  'an_partner',        // Route partner: Partner fertigt und versendet
  'klaerfall',         // gesperrt, Grund Pflicht (status_vor_klaerfall merkt sich den Rueckweg)
  'bereit',            // alles verfuegbar, kann gepickt/gepackt werden
  'in_pick',           // an einem Packplatz geoeffnet
  'gepackt',           // alle Positionen gescannt
  'label_erstellt',    // Label beim Carrier erzeugt
  'uebergeben',        // Tagesabschluss / Partner meldet Versand → Bestand gebucht, Mail raus
  'zugestellt',        // Tracking-Event
  'zustellproblem',    // unzustellbar, Annahme verweigert, Ruecklauf
  'storniert',
] as const;
export type SendungStatus = (typeof SENDUNG_STATUS)[number];

/** Erlaubte Uebergaenge je Ausgangsstatus (Klaerfall zusaetzlich: zurueck in status_vor_klaerfall). */
export const SENDUNG_UEBERGAENGE: Record<SendungStatus, readonly SendungStatus[]> = {
  neu: ['wartet_bestand', 'wartet_produktion', 'an_partner', 'bereit', 'klaerfall', 'storniert'],
  wartet_bestand: ['bereit', 'klaerfall', 'storniert'],
  wartet_produktion: ['bereit', 'wartet_bestand', 'klaerfall', 'storniert'],
  an_partner: ['uebergeben', 'klaerfall', 'storniert'],
  klaerfall: ['neu', 'wartet_bestand', 'wartet_produktion', 'an_partner', 'bereit', 'gepackt', 'storniert'],
  bereit: ['in_pick', 'klaerfall', 'storniert'],
  in_pick: ['gepackt', 'bereit', 'klaerfall', 'storniert'],
  gepackt: ['label_erstellt', 'bereit', 'klaerfall', 'storniert'],
  // klaerfall: Storno, dessen Label der Carrier nicht zuruecknimmt (B5) – das Paket darf nicht in den Tagesabschluss.
  label_erstellt: ['uebergeben', 'gepackt', 'klaerfall', 'storniert'],
  uebergeben: ['zugestellt', 'zustellproblem'],
  zugestellt: [],
  zustellproblem: ['zugestellt'],
  storniert: [],
};

export function darfSendungWechseln(von: SendungStatus, nach: SendungStatus): boolean {
  return SENDUNG_UEBERGAENGE[von].includes(nach);
}

/** Status, in denen die Ware das Haus noch nicht verlassen hat (Storno ohne Rueckbuchung moeglich). */
export const SENDUNG_VOR_UEBERGABE: readonly SendungStatus[] = [
  'neu', 'wartet_bestand', 'wartet_produktion', 'an_partner', 'klaerfall', 'bereit', 'in_pick', 'gepackt', 'label_erstellt',
];

export const SENDUNG_OFFEN: readonly SendungStatus[] = [...SENDUNG_VOR_UEBERGABE];
export const SENDUNG_ABGESCHLOSSEN: readonly SendungStatus[] = ['zugestellt', 'storniert'];

/**
 * `ausland`: Lieferland nicht freigegeben (Entscheidung Inhaber 17.10.2026, R39) – die Leitung entscheidet.
 * `artikel_unbekannt`: eine Position passt zu keinem StoreOS-Artikel (R103); der Produktabgleich prueft solche Klaerfaelle neu.
 * `artikel_ausgelaufen`: der Artikel wird nicht fortgefuehrt (geführte Erstinventur, R126) und hat keinen Bestand mehr –
 * es kommt kein Nachschub, die Leitung klaert mit der Kundin bzw. dem Kunden.
 */
export const KLAERFALL_GRUENDE = ['adresse', 'zahlung', 'bestand', 'kunde_rueckfrage', 'produktion_verzug', 'ausland', 'artikel_unbekannt', 'artikel_ausgelaufen', 'sonstiges'] as const;
export type KlaerfallGrund = (typeof KLAERFALL_GRUENDE)[number];

/** Produktionsstatus einer POSITION (nur Routen intern_bb und partner; Lagerware: nicht_noetig). */
export const PRODUKTIONS_STATUS = ['nicht_noetig', 'offen', 'uebergeben', 'eingegangen', 'fertig'] as const;
export type ProduktionsStatus = (typeof PRODUKTIONS_STATUS)[number];

export const ZAHLUNGSSTATUS = ['offen', 'bezahlt', 'teilerstattet', 'erstattet', 'fehlgeschlagen', 'storniert'] as const;
export type Zahlungsstatus = (typeof ZAHLUNGSSTATUS)[number];

/** Abgeleiteter Auftragsstatus. Wird NICHT gespeichert, sondern berechnet (Ruling R-Status). */
export const AUFTRAG_STATUS = [
  'wartet_zahlung', // Kanalstatus unbezahlt (on-hold/pending): sichtbar, keine Sendung, keine Reservierung
  'offen',          // bezahlt, Sendungen noch nicht in Arbeit
  'klaerfall',      // mindestens eine Sendung im Klaerfall
  'in_arbeit',      // mindestens eine Sendung zwischen bereit und label_erstellt
  'teilversendet',  // mindestens eine uebergeben UND mindestens eine offen
  'versendet',      // alle (nicht stornierten) uebergeben oder zugestellt, nicht alle zugestellt
  'zugestellt',
  'zustellproblem',
  'storniert',
  'erstattet',
] as const;
export type AuftragStatus = (typeof AUFTRAG_STATUS)[number];

export function auftragStatusAus(e: {
  zahlungsstatus: Zahlungsstatus;
  sendungen: ReadonlyArray<{ status: SendungStatus }>;
}): AuftragStatus {
  if (e.zahlungsstatus === 'erstattet') return 'erstattet';
  if (e.zahlungsstatus === 'storniert') return 'storniert';
  if (e.zahlungsstatus === 'offen' || e.zahlungsstatus === 'fehlgeschlagen') return 'wartet_zahlung';
  const aktiv = e.sendungen.filter((s) => s.status !== 'storniert');
  if (e.sendungen.length > 0 && aktiv.length === 0) return 'storniert';
  if (aktiv.length === 0) return 'offen';
  const hat = (st: SendungStatus) => aktiv.some((s) => s.status === st);
  const alle = (f: (s: SendungStatus) => boolean) => aktiv.every((s) => f(s.status));
  if (hat('klaerfall')) return 'klaerfall';
  if (hat('zustellproblem')) return 'zustellproblem';
  if (alle((s) => s === 'zugestellt')) return 'zugestellt';
  const raus = (s: SendungStatus) => s === 'uebergeben' || s === 'zugestellt';
  if (alle(raus)) return 'versendet';
  if (aktiv.some((s) => raus(s.status))) return 'teilversendet';
  if (aktiv.some((s) => ['bereit', 'in_pick', 'gepackt', 'label_erstellt', 'wartet_produktion', 'an_partner'].includes(s.status))) return 'in_arbeit';
  return 'offen';
}

/** Normalisierte Carrier-Status (Recherche 02, Abschnitt 5.1). */
export const CARRIER_STATUS = [
  'label_created', 'picked_up', 'in_transit', 'out_for_delivery', 'awaiting_pickup',
  'delivered', 'exception', 'returned', 'cancelled', 'unknown',
] as const;
export type CarrierStatus = (typeof CARRIER_STATUS)[number];

/** Welcher Sendungsstatus aus einem Carrier-Event folgt (null = Sendung unveraendert, nur Event loggen). */
export function sendungStatusAusCarrier(aktuell: SendungStatus, carrier: CarrierStatus): SendungStatus | null {
  if (aktuell !== 'uebergeben' && aktuell !== 'zustellproblem') return null;
  switch (carrier) {
    case 'delivered': return 'zugestellt';
    case 'exception':
    case 'returned': return aktuell === 'uebergeben' ? 'zustellproblem' : null;
    default: return null;
  }
}

export const RETOURE_STATUS = ['angemeldet', 'eingegangen', 'geprueft', 'erstattet', 'abgelehnt', 'abgeschlossen'] as const;
export type RetoureStatus = (typeof RETOURE_STATUS)[number];

export const RETOURE_ZUSTAENDE = ['A', 'B', 'C'] as const; // A verkaufsfaehig, B Mangel, C Ausschuss
export type RetoureZustand = (typeof RETOURE_ZUSTAENDE)[number];

/**
 * Gruende fuer Lagerbewegungen. Korrekturen brauchen zusaetzlich einen Korrekturgrund.
 * `inventur`: Differenz aus einer freigegebenen Inventur (Blindzaehlung, R38); `inventur_start` bleibt der Seed-/Uebernahmewert.
 * `startbestand_kanal`: Woo-Bestand beim ersten Anlegen eines Artikels im Parallelbetrieb (R101), ohne Platz; die
 * Erstzaehlung zur Umschaltung ersetzt ihn.
 * `produktion_zugang` / `produktion_storno`: on demand gedruckte Module von B&B (R127) und ihre Gegenbuchung (Storno binnen 24 h).
 */
export const BEWEGUNGS_GRUENDE = [
  'inventur_start', 'wareneingang', 'abgang_versand', 'storno_rueckgabe', 'retoure_zugang', 'umlagerung', 'korrektur', 'produktion_verbrauch', 'inventur',
  'startbestand_kanal', 'produktion_zugang', 'produktion_storno',
] as const;
export type BewegungsGrund = (typeof BEWEGUNGS_GRUENDE)[number];

export const KORREKTUR_GRUENDE = [
  'inventurdifferenz', 'bruch', 'muster_eigenverbrauch', 'fehlbuchung', 'retoure_nicht_verkaufsfaehig', 'fund',
] as const;
export type KorrekturGrund = (typeof KORREKTUR_GRUENDE)[number];
